import {
    errors,
    isEmptyGuestsError,
    isGuestError
} from './errorEvents';
import {labelMaker} from '../../server/utils/labelMaker';

function getTotalGuests({adults = 0, children = 0}) {
    return adults + children;
}

function getMaxAdultsFromHouseRules(houseRules) {
    return houseRules && houseRules.maximumOccupancyRule ? houseRules.maximumOccupancyRule.adults : 0;
}

function getMaxGuestsLabel(descriptionLabel, sleeps, maxAdults, translation) {
    if (sleeps !== maxAdults && maxAdults) {
        let countLabel = labelMaker(translation, {ADULT: maxAdults, KID: Math.max(0, sleeps - maxAdults)});
        return `${descriptionLabel} ${countLabel}.`;
    }

    return descriptionLabel;
}

function hasGuests({adults = 0, children = 0}) {
    return getTotalGuests({adults, children}) > 0;
}

function hasTooManyAdults(adults = 0, maxAdults = 0) {
    return maxAdults && adults > maxAdults;
}

function hasTooManyGuests({adults = 0, children = 0, sleeps = 0}) {
    return getTotalGuests({adults, children}) > sleeps;
}

function hasValidGuestCount({adults = 0, children = 0, maxAdults = 0, sleeps = 0, isEmptyValid = true}) {
    if (!isEmptyValid && !hasGuests({adults, children})) {
        return false;
    }

    return !hasTooManyGuests({adults, children, sleeps}) && !hasTooManyAdults(adults, maxAdults);
}

function hasValidPets({hasPets = false, petsAllowed = false}) {
    return !hasPets || (hasPets && petsAllowed);
}

function hasAdults({adults = 0}) {
    return adults > 0;
}

function hasAdultSupervision({adults = 0, children = 0}) {
    return !(children > 0 && adults === 0);
}

function hasValidGuests({
    adults = 0,
    children = 0,
    hasPets = false,
    maxAdults = 0,
    sleeps = 0,
    petsAllowed = false,
    isEmptyValid = true,
    error = {} // most likely a quote error
}) {
    // check if zero guests and zero guests is an invalid state
    if (!isEmptyValid && !hasGuests({adults, children})) {
        return false;
    }

    // check if there's a quote error
    if (isGuestError(error) && !(isEmptyValid && isEmptyGuestsError(error))) {
        return false;
    }

    // otherwise, check the usual suspects
    const validGuestCount = !hasTooManyGuests({adults, children, sleeps});
    const validPets = hasValidPets({hasPets, petsAllowed});
    const validAdults = hasAdultSupervision({adults, children}) && !hasTooManyAdults(adults, maxAdults);

    return validAdults && validGuestCount && validPets;
}

function getSpaceLeft({adults, children, sleeps}) {
    const spaceMargin = sleeps - adults - children;
    return Math.max(0, spaceMargin);
}

function getMaxAdultValue({adults, children, sleeps}) {
    return adults + getSpaceLeft({adults, children, sleeps});
}

function getMaxChildrenValue({adults, children, sleeps}) {
    return children + getSpaceLeft({adults, children, sleeps});
}

/* eslint-disable complexity */
function getGuestErrorObject({
    adults = 0,
    children = 0,
    hasPets = false,
    maxAdults = 0,
    petsAllowed = false,
    sleeps = 0,
    translations = {}
}) {
    let error = null;

    const validGuestCount = !hasTooManyGuests({adults, children, sleeps});
    const validPets = hasValidPets({hasPets, petsAllowed});

    if (!validGuestCount) {
        error = errors.OVER_MAX_OCCUPANCY;
        error.label = labelMaker(translations[error.translationKey], sleeps);
    } else if (!hasAdultSupervision({adults, children})) {
        error = errors.NEEDS_ADULTS;
        error.label = translations[error.translationKey];
    } else if (!validPets) {
        error = errors.PETS_NOT_ALLOWED;
        error.label = translations[error.translationKey];
    } else if (!adults) {
        error = errors.ENTER_GUESTS;
        error.label = translations[error.translationKey];
    } else if (hasTooManyAdults(adults, maxAdults)) {
        error = errors.TOO_MANY_ADULTS;
        error.label = labelMaker(translations[error.translationKey], {MAX: maxAdults});
    }

    return error;
}
/* eslint-enable complexity */

export default hasGuests;
export {
    getGuestErrorObject,
    getMaxAdultsFromHouseRules,
    getMaxAdultValue,
    getMaxChildrenValue,
    getMaxGuestsLabel,
    getSpaceLeft,
    getTotalGuests,
    hasAdults,
    hasAdultSupervision,
    hasGuests,
    hasTooManyAdults,
    hasTooManyGuests,
    hasValidGuests,
    hasValidGuestCount,
    hasValidPets
};
